import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { isEmpty, isEqual, last } from 'lodash';
import Selector from 'components/Form/Selector';
import { isProdEnv } from 'utils/helpers';
import { kilometersToMeters } from 'utils/converter';
import Checkbox from 'components/Form/Checkbox';
import { isIterationSolution } from 'utils/SolutionHelpers';
import Modal from '../../Presentation/Modal';
import {
  clearVehicleParameter,
  setOrders,
  setSolutionJson,
  setTourParameter,
  setUserParam,
} from '../../../actions';
import withTranslation from '../../../hoc/withTranslation';
import {
  StyledContrastPanel,
  StyledContrastPanelColumnsContainer,
  StyledContrastPanelFooterSettings,
  StyledContrastPanelHeader,
} from '../../Wizard/Global/WizardStyledContrastPanel';
import { StyledText } from '../../Wizard/Global/WizardStyled';
import Section from '../../Form/Section';
import { StyledAccessContainer } from '../StyledComponents';
import mapSettings from '../../../global/img/map-settings.svg';
import { PANELS, defaultMapSettings } from '../../../reducers/user';
import Button from '../../Form/Button';
import MultipleActionSetting from './MultipleActionSetting';
import SingleActionSetting from './SingleActionSetting';

const isProd = isProdEnv();

const OPTIONS = { General: 'general', Problem: 'problem', Solution: 'solution' };

const defaultLimitPlaceholders = {
  maxDistance: 'Enter distance in km',
  shiftTime: 'Enter shift time in hr',
  maxDrivingTime: 'Enter max time in hr',
  maxStops: 'Enter max stops',
  minStops: 'Enter min stops',
};

export const MAP_SETTINGS = {
  [OPTIONS.General]: {
    SATELLITE_VIEW: 'satelliteView',
  },
  [OPTIONS.Problem]: {
    ...(isProd
      ? { SHOW_JOB_CLUSTERS: 'showJobClusters' }
      : {
          SHOW_JOB_CLUSTERS: 'showJobClusters',
          CONVERT_TO_TERRITORY: 'convertToTerritory',
          HIDE_POSITIONS: 'hidePositions',
          HIDE_GROUPS: 'hideGroups',
          HIDE_MAX_TIME_ON_VEHICLE: 'hideMaxTimeOnVehicle',
          HIDE_TERRITORIES: 'hideTerritories',
          HIDE_SKILLS: 'hideSkills',
          HIDE_PRIORITY: 'hidePriority',
          HIDE_CATEGORY: 'hideCategory',
          ADD_VEHICLE_LIMIT: 'addVehicleLimit',
          SPEED_FACTOR: 'speedFactor',
          TERRITORIES: 'territories',
          SKILLS: 'skills',
          CATEGORIES: 'categories',
          TRAFFIC: 'traffic',
        }),
  },
  [OPTIONS.Solution]: {
    SHOW_STOPS: 'showStops',
    SHOW_CLUSTERS: 'showStopClusters',
    GRADIENT_ROUTES: 'gradientRoutes',
    SHOW_STRAIGHT_ROUTE_LINES: 'showStraightRouteLines',
    SHOW_TOUR_DIRECTION_ANIMATION: 'showTourDirectionAnimation',
    SHOW_TOUR_CONVEX_HULL: 'showTourConvexHull',
    SHOW_TOUR_CONCAVE_HULL: 'showTourConcaveHull',
    HIDE_ALL_ROUTES: 'hideAllRoutes',
    SHOW_INTERSECTIONS: 'showIntersections',
    SHOW_OVERLAPS: 'showOverlaps',
    HIDE_VISITED: 'hideVisited',
  },
};

const LimitFieldsWrapper = styled.div`
  padding-left: 2rem;
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const LimitFieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: white;
`;

const Label = styled.label`
  width: 160px;
  flex-shrink: 0;
  font-size: 0.85rem; /* smaller font size */
`;

const Input = styled.input`
  background: #222;
  color: white;
  border: 1px solid #444;
  padding: 0.25rem 0.5rem;
  flex: 1;
  font-size: 0.85rem; /* match input font size */
`;

const StyledScrollableContent = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
  padding: 0 1rem 0rem 1rem;
  display: flex;
  flex-direction: column;
`;

const FLEET_KEYS = [
  MAP_SETTINGS.problem.TERRITORIES,
  MAP_SETTINGS.problem.SPEED_FACTOR,
  MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT,
  MAP_SETTINGS.problem.SKILLS,
  MAP_SETTINGS.problem.CATEGORIES,
  MAP_SETTINGS.problem.TRAFFIC,
];

const PLAN_KEYS = [
  MAP_SETTINGS.problem.HIDE_POSITIONS,
  MAP_SETTINGS.problem.HIDE_GROUPS,
  MAP_SETTINGS.problem.HIDE_PRIORITY,
  MAP_SETTINGS.problem.HIDE_MAX_TIME_ON_VEHICLE,
];

export const DEFAULT_TERRITORY_SKILLS_VALUE = 'a1,a2';

const StyledSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: ${(props) => (props.enabled ? 500 : 200)};
  color: white;
  border-bottom: ${(props) => (props.bordered ? '1px solid #222' : 'none')};
  padding-bottom: 1rem;
  padding-top: 1rem;
  padding-left: 0.5rem;
  background-color: #2f353d;
  text-align: center;
`;

const MapSettingsPanel = ({
  user,
  translations: { mapSettingsPanel, solverConfigLabels },
  wizardShown,
}) => {
  const dispatch = useDispatch();
  const solution = useSelector(({ solution: stateSolution }) => stateSolution);
  const tourPlanner = useSelector(
    ({ tourPlanner: stateTourPlanner }) => stateTourPlanner.value[solution.show],
  );
  const orders = useSelector((state) => state.orders[solution.show]);

  const {
    display: { showProblem },
  } = useSelector(({ mapContainer }) => mapContainer);
  const handleSetUserParam = useCallback((data) => dispatch(setUserParam(data)), [dispatch]);
  const handleSetTourParameter = useCallback(
    (parameter, noIdx) =>
      dispatch(setTourParameter(noIdx ? { ...parameter } : { ...parameter, index: solution.show })),
    [dispatch, solution.show],
  );
  const handleClearVehicleParameter = useCallback(
    (parameter) => dispatch(clearVehicleParameter(parameter, solution.show)),
    [dispatch, solution.show],
  );
  const handleSetSolutionJson = useCallback(
    (param) => dispatch(setSolutionJson(param)),
    [dispatch],
  );
  const handleSetOrders = useCallback(
    (newOrders) => dispatch(setOrders(newOrders, false, solution.show)),
    [dispatch, solution.show],
  );
  const addVehicleLimitRef = useRef(null);
  const scrollableContentRef = useRef(null);
  const planSettingsBackupRef = useRef({});
  const speedFactorBackupRef = useRef(null);
  const trafficBackupRef = useRef(null);
  const addVehicleLimitValuesBackupRef = useRef({});
  const addVehicleLimitSwitcherRef = useRef();
  const fleetSettingsBackupRef = useRef({});
  const planSectionRef = useRef(null);

  const [showPanel, setShowPanel] = useState(false);
  const [selectedType, setSelectedType] = useState(OPTIONS.Problem);
  const [maxDistanceLimit, setMaxDistanceLimit] = useState('100');
  const [shiftTimeLimit, setShiftTimeLimit] = useState('8');
  const [maxDrivingTimeLimit, setMaxDrivingTimeLimit] = useState('');
  const [maxStopsLimit, setMaxStopsLimit] = useState('');
  const [minStopsLimit, setMinStopsLimit] = useState('');
  const [fleetEnabled, setFleetEnabled] = useState(
    user.mapSettings.__fleetEnabled !== undefined ? user.mapSettings.__fleetEnabled : null,
  );
  const [planEnabled, setPlanEnabled] = useState(
    user.mapSettings.__planEnabled !== undefined ? user.mapSettings.__planEnabled : false,
  );

  const [jobClustersEnabled, setJobClustersEnabled] = useState(
    !!user.mapSettings[MAP_SETTINGS.problem.SHOW_JOB_CLUSTERS],
  );
  const limitState = {
    maxDistance: [maxDistanceLimit, setMaxDistanceLimit],
    shiftTime: [shiftTimeLimit, setShiftTimeLimit],
    maxDrivingTime: [maxDrivingTimeLimit, setMaxDrivingTimeLimit],
    maxStops: [maxStopsLimit, setMaxStopsLimit],
    minStops: [minStopsLimit, setMinStopsLimit],
  };

  const limitOptions = ['maxDistance', 'shiftTime', 'maxDrivingTime', 'maxStops', 'minStops'];
  const tempOrders = solution.requests[solution.show].tempOrders;
  const isSolution =
    solution && isIterationSolution(last(solution.requests[solution.show].iterations));

  const settings = Object.entries(MAP_SETTINGS[selectedType]).map(([key, value]) => ({
    key,
    value,
  }));
  const togglePanel = useCallback(() => {
    handleSetUserParam({ openPanel: showPanel ? undefined : PANELS.MAP_SETTINGS });
    setShowPanel(!showPanel);
  }, [showPanel, handleSetUserParam]);

  const handleDoneClick = () => {
    setTimeout(() => {
      togglePanel();
    }, 100);
  };

  const handleFleetSwitch = (value) => {
    setFleetEnabled(value);

    const updatedMapSettings = { ...user.mapSettings };
    const fleetKeys = [
      MAP_SETTINGS.problem.TERRITORIES,
      MAP_SETTINGS.problem.SKILLS,
      MAP_SETTINGS.problem.CATEGORIES,
      MAP_SETTINGS.problem.SPEED_FACTOR,
      MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT,
      MAP_SETTINGS.problem.TRAFFIC,
    ];

    if (value === false && !updatedMapSettings.__fleetOriginalEnabledSnapshot) {
      const snapshot = {};
      fleetKeys.forEach((k) => {
        const s = updatedMapSettings[k];
        snapshot[k] = s && typeof s === 'object' ? !!s.enabled : false;
      });
      updatedMapSettings.__fleetOriginalEnabledSnapshot = snapshot;
    }

    if (!value) {
      const backup = {};
      fleetKeys.forEach((key) => {
        let setting = updatedMapSettings[key];
        if (!setting) return;

        if (key === MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT && typeof setting !== 'object') {
          setting = {
            enabled: !!setting,
            value: {
              maxDistance: String(maxDistanceLimit),
              shiftTime: String(shiftTimeLimit),
              maxDrivingTime: String(maxDrivingTimeLimit),
              maxStops: String(maxStopsLimit),
              minStops: String(minStopsLimit),
            },
          };
          updatedMapSettings[key] = setting;
        }

        backup[key] = {
          enabled: !!setting.enabled,
          value: setting.value,
        };

        fleetSettingsBackupRef.current[key] = !!setting.enabled;

        if (key === MAP_SETTINGS.problem.SPEED_FACTOR) {
          speedFactorBackupRef.current = setting.value;
        }
        if (key === MAP_SETTINGS.problem.TRAFFIC) {
          trafficBackupRef.current = setting.value;
        }
        if (key === MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT) {
          backup.vehicleLimits = {
            maxDistance: maxDistanceLimit,
            shiftTime: shiftTimeLimit,
            maxDrivingTime: maxDrivingTimeLimit,
            maxStops: maxStopsLimit,
            minStops: minStopsLimit,
          };
          addVehicleLimitValuesBackupRef.current = { ...backup.vehicleLimits };
        }

        updatedMapSettings[key] = { ...setting, enabled: false };
      });

      updatedMapSettings.__fleetBackup = backup;

      handleSetTourParameter({
        vehicles: [
          {
            ...tourPlanner.vehicles[0],
            limits: {
              maxDistance: { enabled: false, value: '', userChange: true },
              shiftTime: { enabled: false, value: '', userChange: true },
              maxDrivingTime: { enabled: false, value: '', userChange: true },
              maxStops: { enabled: false, value: '', userChange: true },
              minStops: { enabled: false, value: '', userChange: true },
            },
          },
        ],
      });
      handleClearVehicleParameter(['speedFactor']);
      handleClearVehicleParameter(['traffic']);
    } else {
      const backup = updatedMapSettings.__fleetBackup || {};
      const originalSnapshot = updatedMapSettings.__fleetOriginalEnabledSnapshot || {};
      fleetKeys.forEach((key) => {
        if (!updatedMapSettings[key]) return;
        const saved = backup[key];
        if (!saved) return;

        const restoredEnabled =
          fleetSettingsBackupRef.current[key] !== undefined
            ? fleetSettingsBackupRef.current[key]
            : originalSnapshot[key] !== undefined
            ? originalSnapshot[key]
            : saved.enabled !== undefined
            ? saved.enabled
            : false;

        const base = {
          ...updatedMapSettings[key],
          enabled: restoredEnabled,
        };

        if (key === MAP_SETTINGS.problem.SPEED_FACTOR) {
          base.value = speedFactorBackupRef.current ?? saved.value ?? 1;
          base.userChange = true;
          if (!restoredEnabled) base.userChange = false;
        } else if (key === MAP_SETTINGS.problem.TRAFFIC) {
          base.value = trafficBackupRef.current ?? saved.value ?? 'None';
          base.userChange = true;
          if (!restoredEnabled) base.userChange = false;
        } else if (key === MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT) {
          base.value = { ...addVehicleLimitValuesBackupRef.current };
          if (!restoredEnabled) {
            // Keep stored values but disabled
          }
        } else {
          base.value = saved.value;
        }

        updatedMapSettings[key] = base;
      });

      const addLimitEnabled =
        updatedMapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT]?.enabled === true;

      const speedFactorEnabled =
        updatedMapSettings[MAP_SETTINGS.problem.SPEED_FACTOR]?.enabled === true;

      const trafficEnabled = updatedMapSettings[MAP_SETTINGS.problem.TRAFFIC]?.enabled === true;

      const vl =
        addLimitEnabled &&
        (updatedMapSettings.__fleetBackup?.vehicleLimits || backup.vehicleLimits || null);

      const limits = {};
      if (vl) {
        if (vl.maxDistance && vl.maxDistance !== '0') {
          limits.maxDistance = {
            enabled: true,
            value: kilometersToMeters(Number(vl.maxDistance)),
            userChange: true,
          };
        }
        if (vl.shiftTime && vl.shiftTime !== '0') {
          limits.shiftTime = {
            enabled: true,
            value: Number(vl.shiftTime) * 3600,
            userChange: true,
          };
        }
        if (vl.maxDrivingTime && vl.maxDrivingTime !== '0') {
          limits.maxDrivingTime = {
            enabled: true,
            value: Number(vl.maxDrivingTime) * 3600,
            userChange: true,
          };
        }
        if (vl.maxStops && vl.maxStops !== '0') {
          limits.maxStops = {
            enabled: true,
            value: Number(vl.maxStops),
            userChange: true,
          };
        }
        if (vl.minStops && vl.minStops !== '0') {
          limits.minStops = {
            enabled: true,
            value: Number(vl.minStops),
            userChange: true,
          };
        }
      }

      const vehiclePatch = {
        ...(addLimitEnabled && Object.keys(limits).length ? { limits } : {}),
        ...(speedFactorEnabled
          ? {
              speedFactor:
                speedFactorBackupRef.current ??
                updatedMapSettings[MAP_SETTINGS.problem.SPEED_FACTOR]?.value ??
                1,
            }
          : {}),
        ...(trafficEnabled
          ? {
              traffic:
                trafficBackupRef.current ??
                updatedMapSettings[MAP_SETTINGS.problem.TRAFFIC]?.value ??
                'None',
            }
          : {}),
      };

      if (Object.keys(vehiclePatch).length) {
        handleSetTourParameter({
          vehicles: [
            {
              ...tourPlanner.vehicles[0],
              ...vehiclePatch,
            },
          ],
        });
      }
    }

    handleSetUserParam({
      mapSettings: {
        ...updatedMapSettings,
        __fleetEnabled: value,
      },
      fleetEnabled: value,
    });
  };

  const handlePlanSwitch = (value) => {
    setPlanEnabled(value);

    const updatedMapSettings = { ...user.mapSettings };

    if (!value) {
      const planBackup = {};
      [
        MAP_SETTINGS.problem.HIDE_POSITIONS,
        MAP_SETTINGS.problem.HIDE_GROUPS,
        MAP_SETTINGS.problem.HIDE_PRIORITY,
        MAP_SETTINGS.problem.HIDE_MAX_TIME_ON_VEHICLE,
      ].forEach((key) => {
        planBackup[key] = updatedMapSettings[key];
        updatedMapSettings[key] = false;
      });
      updatedMapSettings.__planBackup = planBackup;
    } else {
      const planBackup = updatedMapSettings.__planBackup || {};
      [
        MAP_SETTINGS.problem.HIDE_POSITIONS,
        MAP_SETTINGS.problem.HIDE_GROUPS,
        MAP_SETTINGS.problem.HIDE_PRIORITY,
        MAP_SETTINGS.problem.HIDE_MAX_TIME_ON_VEHICLE,
      ].forEach((key) => {
        updatedMapSettings[key] = planBackup[key] !== undefined ? planBackup[key] : false;
      });
    }

    handleSetUserParam({
      mapSettings: {
        ...updatedMapSettings,
        __planEnabled: value,
      },
      planEnabled: value,
    });
  };

  const handleJobClustersSwitch = (value) => {
    setJobClustersEnabled(value);
    handleSetUserParam({
      mapSettings: {
        ...user.mapSettings,
        [MAP_SETTINGS.problem.SHOW_JOB_CLUSTERS]: value,
      },
    });
  };

  const handleOnSwitchChange = useCallback(
    (id, value) => {
      const isConvertToTerritory = id === MAP_SETTINGS[OPTIONS.Problem].CONVERT_TO_TERRITORY;
      const isConcave = id === MAP_SETTINGS[OPTIONS.Solution].SHOW_TOUR_CONCAVE_HULL;
      const showStopsOff = id === MAP_SETTINGS[OPTIONS.Solution].SHOW_STOPS && !value;
      const showConvexHull = id === MAP_SETTINGS[OPTIONS.Solution].SHOW_TOUR_CONVEX_HULL && value;
      const showConcaveHull = isConcave && value;
      const isSpeedFactor = id === MAP_SETTINGS[OPTIONS.Problem].SPEED_FACTOR;
      const isTraffic = id === MAP_SETTINGS[OPTIONS.Problem].TRAFFIC;

      const isAddVehicleLimit = id === MAP_SETTINGS[OPTIONS.Problem].ADD_VEHICLE_LIMIT;
      const isTerritories = id === MAP_SETTINGS[OPTIONS.Problem].TERRITORIES;
      const isSkills = id === MAP_SETTINGS[OPTIONS.Problem].SKILLS;
      const isCategories = id === MAP_SETTINGS[OPTIONS.Problem].CATEGORIES;
      const isHidePositions = id === MAP_SETTINGS[OPTIONS.Problem].HIDE_POSITIONS;
      const isHideGroups = id === MAP_SETTINGS[OPTIONS.Problem].HIDE_GROUPS;
      const isHidePriority = id === MAP_SETTINGS[OPTIONS.Problem].HIDE_PRIORITY;
      const isHideMaxTimeOnVehicle = id === MAP_SETTINGS[OPTIONS.Problem].HIDE_MAX_TIME_ON_VEHICLE;

      let payload;

      if (isAddVehicleLimit) {
        payload = {
          enabled: value,
          userChange: true,
          value: {
            maxDistance: String(maxDistanceLimit),
            shiftTime: String(shiftTimeLimit),
            maxDrivingTime: String(maxDrivingTimeLimit),
            maxStops: String(maxStopsLimit),
            minStops: String(minStopsLimit),
          },
        };
      } else if (isConvertToTerritory) {
        payload = value ? 'enabled' : null;
      } else if (
        isConcave ||
        isSpeedFactor ||
        isTraffic ||
        isTerritories ||
        isSkills ||
        isCategories
      ) {
        payload = {
          ...user.mapSettings[id],
          enabled: value,
          userChange: true,
          ...(!user.mapSettings[id]?.value &&
            (isTerritories ? { value: DEFAULT_TERRITORY_SKILLS_VALUE } : {})),
          ...(!user.mapSettings[id]?.value &&
            (isSkills ? { value: DEFAULT_TERRITORY_SKILLS_VALUE } : {})),
          ...(!user.mapSettings[id]?.value &&
            (isCategories ? { value: DEFAULT_TERRITORY_SKILLS_VALUE } : {})),
          ...(!user.mapSettings[id]?.value &&
            (isTraffic
              ? { value: 'None' }
              : !isTerritories && !isSkills && !isCategories
              ? { value: 2 }
              : {})),
        };
      } else {
        payload = value;
      }

      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [id]: payload,
          ...(isAddVehicleLimit ? { isUserChange: true } : {}),
          ...(isHidePositions ? { positionsChange: true } : {}),
          ...(isHideGroups ? { groupsChange: true } : {}),
          ...(isHideMaxTimeOnVehicle ? { maxTimeChange: true } : {}),
          ...(isHidePriority ? { priorityChange: true } : {}),
          ...(showStopsOff && { [MAP_SETTINGS[OPTIONS.Solution].SHOW_CLUSTERS]: false }),
          ...(showConvexHull && {
            [MAP_SETTINGS[OPTIONS.Solution].SHOW_TOUR_CONCAVE_HULL]: false,
          }),
          ...(showConcaveHull && {
            [MAP_SETTINGS[OPTIONS.Solution].SHOW_TOUR_CONVEX_HULL]: false,
          }),
        },
      });

      if (isSpeedFactor && payload.enabled) {
        handleSetTourParameter({
          vehicles: [
            {
              ...tourPlanner.vehicles[0],
              speedFactor: payload.value ?? 1,
            },
          ],
        });
      } else if (isSpeedFactor && !payload.enabled) {
        handleClearVehicleParameter(['speedFactor']);
      }

      if (isTraffic && payload.enabled) {
        handleSetTourParameter({
          vehicles: [
            {
              ...tourPlanner.vehicles[0],
              traffic: payload.value ?? 'None',
            },
          ],
        });
      } else if (isTraffic && !payload.enabled) {
        handleClearVehicleParameter(['traffic']);
      }

      if (isAddVehicleLimit) {
        addVehicleLimitSwitcherRef.current = value;

        if (value) {
          const newLimits = { ...tourPlanner.vehicles[0].limits };

          const toNum = (v) => (v === '' || v == null ? null : Number(v));

          const md = toNum(maxDistanceLimit);
          newLimits.maxDistance = md
            ? {
                enabled: true,
                value: kilometersToMeters(md),
                userChange: true,
              }
            : { enabled: false, value: null, userChange: true };

          const st = toNum(shiftTimeLimit);
          newLimits.shiftTime = st
            ? {
                enabled: true,
                value: st * 3600,
                userChange: true,
              }
            : { enabled: false, value: null, userChange: true };

          const mdt = toNum(maxDrivingTimeLimit);
          if (mdt) {
            newLimits.maxDrivingTime = {
              enabled: true,
              value: mdt * 3600,
              userChange: true,
            };
          } else {
            delete newLimits.maxDrivingTime;
          }

          const mxs = toNum(maxStopsLimit);
          if (mxs && mxs > 0) {
            newLimits.maxStops = {
              enabled: true,
              value: mxs,
              userChange: true,
            };
            newLimits.stops = {
              ...(newLimits.stops || {}),
              maxCount: { value: mxs },
              userChange: true,
              ...(newLimits.stops?.minCount ? { minCount: newLimits.stops.minCount } : {}),
            };
          } else {
            delete newLimits.maxStops;
            if (newLimits.stops) {
              delete newLimits.stops.maxCount;
              if (Object.keys(newLimits.stops).length === 1 && newLimits.stops.minCount == null) {
                delete newLimits.stops;
              }
            }
          }

          const mns = toNum(minStopsLimit);
          if (mns != null && mns >= 0) {
            newLimits.minStops = {
              enabled: true,
              value: mns,
              userChange: true,
            };
            newLimits.stops = {
              ...(newLimits.stops || {}),
              minCount: { value: mns },
              userChange: true,
              ...(newLimits.stops?.maxCount ? { maxCount: newLimits.stops.maxCount } : {}),
            };
          } else {
            delete newLimits.minStops;
            if (newLimits.stops) {
              delete newLimits.stops.minCount;
              if (Object.keys(newLimits.stops).length === 1 && newLimits.stops.maxCount == null) {
                delete newLimits.stops;
              }
            }
          }

          handleSetTourParameter({
            vehicles: [
              {
                ...tourPlanner.vehicles[0],
                ...(Object.keys(newLimits).length ? { limits: newLimits } : {}),
              },
            ],
          });
        } else {
          handleSetTourParameter({
            vehicles: [
              {
                ...tourPlanner.vehicles[0],
                limits: {
                  maxDistance: { enabled: false, value: '', userChange: true },
                  shiftTime: { enabled: false, value: '', userChange: true },
                  maxDrivingTime: { enabled: false, value: '', userChange: true },
                  maxStops: { enabled: false, value: '', userChange: true },
                  minStops: { enabled: false, value: '', userChange: true },
                },
              },
            ],
          });
        }
      }
    },
    [
      handleSetUserParam,
      user.mapSettings,
      tourPlanner,
      handleSetTourParameter,
      handleClearVehicleParameter,
      maxDistanceLimit,
      shiftTimeLimit,
      maxDrivingTimeLimit,
      maxStopsLimit,
      minStopsLimit,
    ],
  );

  const handleOnChangeInput = useCallback(
    (id, value) => {
      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [id]: { ...user.mapSettings[id], value, userChange: true, enabled: true },
        },
      });
      if (id === 'territories' || id === 'skills' || id === 'categories')
        handleSetSolutionJson({ jsonTriggerVRP: true });

      if (id === MAP_SETTINGS.problem.SPEED_FACTOR) {
        handleSetTourParameter({
          vehicles: [
            {
              ...tourPlanner.vehicles[0],
              speedFactor: value ?? 1,
            },
          ],
        });
      }
      if (id === MAP_SETTINGS.problem.TRAFFIC) {
        handleSetTourParameter({
          vehicles: [
            {
              ...tourPlanner.vehicles[0],
              traffic: value ?? 'None',
            },
          ],
        });
      }
    },
    [
      user.mapSettings,
      handleSetUserParam,
      tourPlanner,
      handleSetTourParameter,
      handleSetSolutionJson,
    ],
  );

  const handleOnChangeLimitInput = useCallback(
    (fieldKey, rawValue) => {
      let value = rawValue;

      if (value === '') {
        switch (fieldKey) {
          case 'maxDistance':
            setMaxDistanceLimit('');
            break;
          case 'shiftTime':
            setShiftTimeLimit('');
            break;
          case 'maxDrivingTime':
            setMaxDrivingTimeLimit('');
            break;
          case 'maxStops':
            setMaxStopsLimit('');
            break;
          case 'minStops':
            setMinStopsLimit('');
            break;
          default:
            break;
        }

        const vehicle = tourPlanner.vehicles?.[0];
        const limits = { ...(vehicle?.limits || {}) };
        delete limits[fieldKey];

        if (fieldKey === 'maxStops' && limits.stops) {
          delete limits.stops.maxCount;
          if (Object.keys(limits.stops).length === 0) {
            delete limits.stops;
          }
        }
        if (fieldKey === 'minStops' && limits.stops) {
          delete limits.stops.minCount;
          if (Object.keys(limits.stops).length === 0) {
            delete limits.stops;
          }
        }

        handleSetTourParameter({
          vehicles: [
            {
              ...vehicle,
              limits,
            },
          ],
        });
        return;
      }

      if (
        ['maxDistance', 'shiftTime', 'maxDrivingTime'].includes(fieldKey) &&
        value &&
        parseFloat(value) < 0
      ) {
        value = '0';
      }
      if (['shiftTime', 'maxDrivingTime'].includes(fieldKey) && value && parseFloat(value) > 24) {
        value = '12';
      }
      if (fieldKey === 'maxStops' && value && parseInt(value, 10) < 1) {
        value = '1';
      }
      if (fieldKey === 'minStops' && value && parseInt(value, 10) < 0) {
        value = '0';
      }
      if (fieldKey === 'maxDistance' && value && parseFloat(value) > 3000) {
        value = '100';
      }

      switch (fieldKey) {
        case 'maxDistance':
          setMaxDistanceLimit(value);
          break;
        case 'shiftTime':
          setShiftTimeLimit(value);
          break;
        case 'maxDrivingTime':
          setMaxDrivingTimeLimit(value);
          break;
        case 'maxStops':
          setMaxStopsLimit(value);
          break;
        case 'minStops':
          setMinStopsLimit(value);
          break;
        default:
          break;
      }

      let convertedValue;
      if (fieldKey === 'maxDistance') {
        convertedValue = kilometersToMeters(parseFloat(value));
      } else if (fieldKey === 'shiftTime' || fieldKey === 'maxDrivingTime') {
        convertedValue = parseFloat(value) * 3600;
      } else if (fieldKey === 'maxStops' || fieldKey === 'minStops') {
        convertedValue = parseInt(value, 10);
      } else {
        convertedValue = value;
      }

      const vehicle = tourPlanner.vehicles?.[0];
      const limits = { ...(vehicle?.limits || {}) };
      limits[fieldKey] = {
        enabled: true,
        userChange: true,
        value: convertedValue,
      };

      if (fieldKey === 'maxStops') {
        limits.stops = {
          ...(limits.stops || {}),
          userChange: true,
          maxCount: { value: parseInt(value, 10) },
        };
      }
      if (fieldKey === 'minStops') {
        limits.stops = {
          ...(limits.stops || {}),
          userChange: true,
          minCount: { value: parseInt(value, 10) },
        };
      }

      handleSetTourParameter({
        vehicles: [
          {
            ...vehicle,
            limits,
          },
        ],
      });
    },
    [
      setMaxDistanceLimit,
      setShiftTimeLimit,
      setMaxDrivingTimeLimit,
      setMaxStopsLimit,
      setMinStopsLimit,
      tourPlanner,
      handleSetTourParameter,
    ],
  );

  const onResetDefault = useCallback(() => {
    const fleetUnchanged = FLEET_KEYS.every(
      (key) =>
        user.mapSettings[key]?.enabled === defaultMapSettings[key]?.enabled &&
        user.mapSettings[key]?.value === defaultMapSettings[key]?.value,
    );
    const planUnchanged = PLAN_KEYS.every(
      (key) => user.mapSettings[key] === defaultMapSettings[key],
    );

    if (fleetUnchanged && planUnchanged && selectedType === OPTIONS.Problem) {
      setFleetEnabled(false);
      setPlanEnabled(false);
      setJobClustersEnabled(true);

      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [MAP_SETTINGS.problem.SHOW_JOB_CLUSTERS]: true,
          fleetEnabled: false,
          planEnabled: false,
          __fleetEnabled: false,
          __planEnabled: false,
        },
        fleetEnabled: false,
        planEnabled: false,
      });
      return;
    }

    const resetSettings = Object.keys(MAP_SETTINGS[selectedType]).map(
      (k) => MAP_SETTINGS[selectedType][k],
    );
    const filteredSettings = Object.keys(defaultMapSettings)
      .filter((setting) => resetSettings.includes(setting))
      .reduce((obj, key) => {
        if (selectedType === OPTIONS.Solution) {
          obj[key] = { ...defaultMapSettings[key] };
        } else {
          obj[key] = {
            ...defaultMapSettings[key],
            ...(user.mapSettings[key]?.value !== undefined
              ? { value: user.mapSettings[key].value }
              : {}),
            ...(user.mapSettings[key]?.enabled !== undefined
              ? { enabled: user.mapSettings[key].enabled }
              : {}),
          };
        }
        return obj;
      }, {});

    if (selectedType === OPTIONS.Problem) {
      speedFactorBackupRef.current = user.mapSettings[MAP_SETTINGS.problem.SPEED_FACTOR]?.value;
      trafficBackupRef.current = user.mapSettings[MAP_SETTINGS.problem.TRAFFIC]?.value;
      fleetSettingsBackupRef.current[MAP_SETTINGS.problem.SPEED_FACTOR] =
        user.mapSettings?.[MAP_SETTINGS.problem.SPEED_FACTOR]?.enabled;
      fleetSettingsBackupRef.current[MAP_SETTINGS.problem.TRAFFIC] =
        user.mapSettings?.[MAP_SETTINGS.problem.TRAFFIC]?.enabled;
      fleetSettingsBackupRef.current[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT] =
        (typeof user.mapSettings?.[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT] === 'object'
          ? user.mapSettings?.[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT]?.enabled
          : user.mapSettings?.[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT]) || false;

      addVehicleLimitValuesBackupRef.current = {
        maxDistance: maxDistanceLimit,
        shiftTime: shiftTimeLimit,
        maxDrivingTime: maxDrivingTimeLimit,
        maxStops: maxStopsLimit,
        minStops: minStopsLimit,
      };

      const fleetBackup = {
        ...(user.mapSettings.__fleetBackup || {}),
      };

      const prevAVL = user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT];
      fleetBackup[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT] = {
        enabled:
          (typeof prevAVL === 'object' ? prevAVL.enabled : !!prevAVL) ||
          fleetSettingsBackupRef.current[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT] ||
          false,
        value: (typeof prevAVL === 'object' && prevAVL.value) || {
          ...addVehicleLimitValuesBackupRef.current,
        },
      };

      const prevSF = user.mapSettings[MAP_SETTINGS.problem.SPEED_FACTOR];
      if (prevSF) {
        fleetBackup[MAP_SETTINGS.problem.SPEED_FACTOR] = {
          enabled: prevSF.enabled,
          value: prevSF.value,
        };
      }
      const prevTraffic = user.mapSettings[MAP_SETTINGS.problem.TRAFFIC];
      if (prevTraffic) {
        fleetBackup[MAP_SETTINGS.problem.TRAFFIC] = {
          enabled: prevTraffic.enabled,
          value: prevTraffic.value,
        };
      }
      fleetBackup.vehicleLimits = { ...addVehicleLimitValuesBackupRef.current };

      planSettingsBackupRef.current[MAP_SETTINGS.problem.HIDE_POSITIONS] =
        user.mapSettings[MAP_SETTINGS.problem.HIDE_POSITIONS];
      planSettingsBackupRef.current[MAP_SETTINGS.problem.HIDE_GROUPS] =
        user.mapSettings[MAP_SETTINGS.problem.HIDE_GROUPS];
      planSettingsBackupRef.current[MAP_SETTINGS.problem.HIDE_PRIORITY] =
        user.mapSettings[MAP_SETTINGS.problem.HIDE_PRIORITY];
      planSettingsBackupRef.current[MAP_SETTINGS.problem.HIDE_MAX_TIME_ON_VEHICLE] =
        user.mapSettings[MAP_SETTINGS.problem.HIDE_MAX_TIME_ON_VEHICLE];

      filteredSettings[MAP_SETTINGS.problem.SPEED_FACTOR] = {
        ...filteredSettings[MAP_SETTINGS.problem.SPEED_FACTOR],
        enabled: false,
        value: '',
      };
      filteredSettings[MAP_SETTINGS.problem.TRAFFIC] = {
        ...filteredSettings[MAP_SETTINGS.problem.TRAFFIC],
        enabled: false,
        value: 'None',
      };

      filteredSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT] = {
        enabled: false,
        value: {
          maxDistance: addVehicleLimitValuesBackupRef.current.maxDistance ?? '100',
          shiftTime: addVehicleLimitValuesBackupRef.current.shiftTime ?? '8',
          maxDrivingTime: addVehicleLimitValuesBackupRef.current.maxDrivingTime ?? '',
          maxStops: addVehicleLimitValuesBackupRef.current.maxStops ?? '',
          minStops: addVehicleLimitValuesBackupRef.current.minStops ?? '',
        },
      };

      filteredSettings[MAP_SETTINGS.problem.HIDE_POSITIONS] = false;
      filteredSettings[MAP_SETTINGS.problem.HIDE_GROUPS] = false;
      filteredSettings[MAP_SETTINGS.problem.HIDE_PRIORITY] = false;

      filteredSettings.__fleetBackup = fleetBackup;
      filteredSettings.__planBackup = {
        [MAP_SETTINGS.problem.HIDE_POSITIONS]:
          planSettingsBackupRef.current[MAP_SETTINGS.problem.HIDE_POSITIONS],
        [MAP_SETTINGS.problem.HIDE_GROUPS]:
          planSettingsBackupRef.current[MAP_SETTINGS.problem.HIDE_GROUPS],
        [MAP_SETTINGS.problem.HIDE_PRIORITY]:
          planSettingsBackupRef.current[MAP_SETTINGS.problem.HIDE_PRIORITY],
        [MAP_SETTINGS.problem.HIDE_MAX_TIME_ON_VEHICLE]:
          planSettingsBackupRef.current[MAP_SETTINGS.problem.HIDE_MAX_TIME_ON_VEHICLE],
      };

      setFleetEnabled(false);
      setPlanEnabled(false);
      setJobClustersEnabled(true);

      handleSetTourParameter({
        vehicles: [
          {
            ...tourPlanner.vehicles[0],
            limits: {
              maxDistance: { enabled: false, value: '', userChange: true },
              shiftTime: { enabled: false, value: '', userChange: true },
              maxDrivingTime: { enabled: false, value: '', userChange: true },
              maxStops: { enabled: false, value: '', userChange: true },
              minStops: { enabled: false, value: '', userChange: true },
            },
          },
        ],
      });
      handleClearVehicleParameter(['speedFactor']);

      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          ...filteredSettings,
          [MAP_SETTINGS.problem.SHOW_JOB_CLUSTERS]: true,
          fleetEnabled: false,
          planEnabled: false,
          __fleetEnabled: false,
          __planEnabled: false,
        },
        fleetEnabled: false,
        planEnabled: false,
      });
      return;
    }

    if (selectedType === OPTIONS.Solution) {
      Object.keys(filteredSettings).forEach((key) => {
        if (
          filteredSettings[key] &&
          typeof filteredSettings[key] === 'object' &&
          Object.keys(filteredSettings[key]).length === 0
        ) {
          filteredSettings[key] = false;
        }
      });
      filteredSettings[MAP_SETTINGS.solution.SHOW_STOPS] = true;
      filteredSettings[MAP_SETTINGS.solution.SHOW_CLUSTERS] = true;

      filteredSettings[MAP_SETTINGS.general.SATELLITE_VIEW] =
        user.mapSettings[MAP_SETTINGS.general.SATELLITE_VIEW];
    }

    if (selectedType === OPTIONS.General) {
      filteredSettings[MAP_SETTINGS.general.SATELLITE_VIEW] = false;
    }

    if (
      selectedType === OPTIONS.Problem &&
      Object.prototype.hasOwnProperty.call(
        filteredSettings,
        MAP_SETTINGS[OPTIONS.Problem].CONVERT_TO_TERRITORY,
      )
    ) {
      filteredSettings[MAP_SETTINGS[OPTIONS.Problem].CONVERT_TO_TERRITORY] = null;
    }

    handleSetUserParam({
      mapSettings:
        selectedType === OPTIONS.Solution || selectedType === OPTIONS.General
          ? { ...user.mapSettings, ...filteredSettings }
          : {
              ...user.mapSettings,
              ...filteredSettings,
              [MAP_SETTINGS.problem.SHOW_JOB_CLUSTERS]: true,
              fleetEnabled: false,
              planEnabled: false,
              __fleetEnabled: false,
              __planEnabled: false,
            },
      fleetEnabled: false,
      planEnabled: false,
    });
  }, [
    handleSetUserParam,
    selectedType,
    user.mapSettings,
    fleetEnabled,
    planEnabled,
    jobClustersEnabled,
    tourPlanner,
    handleSetTourParameter,
    maxDistanceLimit,
    shiftTimeLimit,
    maxDrivingTimeLimit,
    maxStopsLimit,
    minStopsLimit,
  ]);

  const types = Object.keys(OPTIONS).map((key) => ({ key: OPTIONS[key], value: key }));

  const onTypeChange = useCallback(
    (value) => {
      setSelectedType(value);
    },
    [setSelectedType],
  );

  useEffect(() => {
    setShowPanel(user.openPanel === PANELS.MAP_SETTINGS);
  }, [user.openPanel, setShowPanel]);

  useEffect(() => {
    if (!showProblem) onTypeChange(OPTIONS.Solution);
    else onTypeChange(OPTIONS.Problem);
  }, [showProblem, onTypeChange]);

  useEffect(() => {
    if (user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT] && addVehicleLimitRef.current) {
      addVehicleLimitRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      addVehicleLimitRef.current = null;
    }
  }, [user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT]]);

  useEffect(() => {
    const limits = tourPlanner.vehicles?.[0]?.limits || {};

    if (
      typeof limits.maxDistance?.value === 'number' &&
      !Number.isNaN(limits.maxDistance.value) &&
      limits.maxDistance.value !== 0
    ) {
      setMaxDistanceLimit(limits.maxDistance.value / 1000);
    } else {
      setMaxDistanceLimit('100');
    }

    if (
      typeof limits.shiftTime?.value === 'number' &&
      !Number.isNaN(limits.shiftTime.value) &&
      limits.shiftTime.value !== 0
    ) {
      setShiftTimeLimit(limits.shiftTime.value / 3600);
    } else {
      setShiftTimeLimit('8');
    }

    if (
      typeof limits.maxDrivingTime?.value === 'number' &&
      !Number.isNaN(limits.maxDrivingTime.value) &&
      limits.maxDrivingTime.value !== 0
    ) {
      setMaxDrivingTimeLimit(String(limits.maxDrivingTime.value / 3600));
    } else {
      setMaxDrivingTimeLimit('');
    }

    const maxStopsVal = limits.maxStops?.value ?? limits.stops?.maxCount?.value;
    if (typeof maxStopsVal === 'number' && !Number.isNaN(maxStopsVal) && maxStopsVal !== 0) {
      setMaxStopsLimit(maxStopsVal);
    } else {
      setMaxStopsLimit('');
    }

    const minStopsVal = limits.minStops?.value ?? limits.stops?.minCount?.value;
    if (typeof minStopsVal === 'number' && !Number.isNaN(minStopsVal) && minStopsVal !== 0) {
      setMinStopsLimit(minStopsVal);
    } else {
      setMinStopsLimit('');
    }
  }, [
    tourPlanner.vehicles?.[0]?.limits?.maxDistance,
    tourPlanner.vehicles?.[0]?.limits?.shiftTime,
    tourPlanner.vehicles?.[0]?.limits?.maxDrivingTime,
    tourPlanner.vehicles?.[0]?.limits?.maxStops,
    tourPlanner.vehicles?.[0]?.limits?.minStops,
    tourPlanner.vehicles?.[0]?.limits?.stops,
    user.mapSettings[MAP_SETTINGS.problem.SPEED_FACTOR],
  ]);

  useEffect(() => {
    const sfSetting = user.mapSettings[MAP_SETTINGS.problem.SPEED_FACTOR];
    const vehicleSF = tourPlanner.vehicles[0].speedFactor;

    if (
      sfSetting &&
      sfSetting.enabled &&
      sfSetting.value !== undefined &&
      sfSetting.value !== '' &&
      vehicleSF !== sfSetting.value
    ) {
      handleSetTourParameter({
        vehicles: [
          {
            ...tourPlanner.vehicles[0],
            speedFactor: sfSetting.value,
          },
        ],
      });
      return;
    }

    if (
      (!sfSetting || sfSetting.value === undefined) &&
      (vehicleSF === null || vehicleSF === undefined || vehicleSF === '')
    ) {
      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [MAP_SETTINGS.problem.SPEED_FACTOR]: {
            enabled: false,
            value: 2,
            userChange: false,
          },
        },
      });
    }
  }, [
    user.mapSettings[MAP_SETTINGS.problem.SPEED_FACTOR],
    tourPlanner.vehicles[0].speedFactor,
    handleSetUserParam,
    handleSetTourParameter,
    handleClearVehicleParameter,
    tourPlanner.vehicles,
    user.mapSettings,
  ]);

  useEffect(() => {
    const trafficSetting = user.mapSettings[MAP_SETTINGS.problem.TRAFFIC];
    const vehicleTraffic = tourPlanner.vehicles[0].traffic;

    if (
      trafficSetting &&
      trafficSetting.enabled &&
      trafficSetting.value &&
      vehicleTraffic !== trafficSetting.value
    ) {
      handleSetTourParameter({
        vehicles: [
          {
            ...tourPlanner.vehicles[0],
            traffic: trafficSetting.value,
          },
        ],
      });
      return;
    }

    if (
      (!trafficSetting || trafficSetting.value === undefined) &&
      (vehicleTraffic === null || vehicleTraffic === undefined || vehicleTraffic === '')
    ) {
      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [MAP_SETTINGS.problem.TRAFFIC]: {
            enabled: false,
            value: 'None',
            userChange: false,
          },
        },
      });
    }
  }, [
    user.mapSettings[MAP_SETTINGS.problem.TRAFFIC],
    tourPlanner.vehicles[0].traffic,
    handleSetUserParam,
    handleSetTourParameter,
    tourPlanner.vehicles,
    user.mapSettings,
  ]);

  useEffect(() => {
    const trafficSetting = user.mapSettings[MAP_SETTINGS.problem.TRAFFIC];
    if (trafficSetting?.value === 'None' && trafficSetting?.enabled && !isSolution) {
      handleSetSolutionJson({ jsonTriggerVRP: true });
    }
  }, [user.mapSettings[MAP_SETTINGS.problem.TRAFFIC], isSolution]);

  useEffect(() => {
    if (planEnabled && planSectionRef.current) {
      planSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [planEnabled]);

  useEffect(() => {
    if (selectedType === OPTIONS.Solution && scrollableContentRef.current) {
      scrollableContentRef.current.scrollTop = 0;
    }
  }, [selectedType]);

  useEffect(() => {
    if (user.mapSettings.__fleetEnabled !== undefined) {
      setFleetEnabled(user.mapSettings.__fleetEnabled);
      handleSetUserParam({ fleetEnabled: user.mapSettings.__fleetEnabled });
    }
    if (user.mapSettings.__planEnabled !== undefined) {
      setPlanEnabled(user.mapSettings.__planEnabled);
      handleSetUserParam({ planEnabled: user.mapSettings.__planEnabled });
    }
  }, [user.mapSettings.__fleetEnabled, user.mapSettings.__planEnabled]);

  useEffect(() => {
    if (fleetEnabled === false) {
      const ords = isEmpty(orders) ? tempOrders : orders;
      const sanitizedOrders = ords?.map((order) => {
        const { Territories, Skills, Category, ...rest } = order;
        return rest;
      });
      if (!isEqual(ords, sanitizedOrders)) handleSetOrders(sanitizedOrders);
    }
  }, [orders, tempOrders, fleetEnabled, handleSetOrders]);

  useEffect(() => {
    const setting = user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT];
    if (setting === true) {
      addVehicleLimitSwitcherRef.current = true;
      return;
    }
    if (setting && typeof setting === 'object') {
      addVehicleLimitSwitcherRef.current = !!setting.enabled;
    }
  }, [
    user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT],
    handleSetUserParam,
    user.mapSettings,
    maxDistanceLimit,
    shiftTimeLimit,
    maxDrivingTimeLimit,
    maxStopsLimit,
    minStopsLimit,
  ]);

  // TODO remove satellite view (and the general tab(

  return (
    <>
      <Modal isDark isVisible={showPanel} isFull fromTop>
        <StyledContrastPanel fullDisplay data-test-id="mapSettingsPanel" column>
          <StyledContrastPanelHeader size="0">
            <StyledText bold contrast>
              {mapSettingsPanel.mapSettingsTitle}
            </StyledText>
          </StyledContrastPanelHeader>
          <StyledScrollableContent ref={scrollableContentRef}>
            <Section extraPaddingTop>
              <Selector
                id="button-type-select"
                initialValue={selectedType}
                options={types}
                inverted
                onChange={onTypeChange}
                disabledTitle={mapSettingsPanel.solutionOptionDisabled}
              />
            </Section>
            {selectedType === OPTIONS.Problem ? (
              <>
                <StyledSectionHeader enabled={jobClustersEnabled} bordered>
                  {mapSettingsPanel.jobClustersSectionTitle}
                  <Checkbox
                    id="job-clusters-master-switch"
                    value={jobClustersEnabled}
                    label=""
                    onChange={handleJobClustersSwitch}
                    contrast
                  />
                </StyledSectionHeader>
                {!isProd && (
                  <StyledSectionHeader enabled={fleetEnabled} bordered={!fleetEnabled}>
                    {mapSettingsPanel.fleetSectionTitle}
                    <Checkbox
                      id="fleet-master-switch"
                      value={fleetEnabled}
                      label=""
                      onChange={handleFleetSwitch}
                      contrast
                    />
                  </StyledSectionHeader>
                )}
                {fleetEnabled && (
                  <Section>
                    <StyledContrastPanelColumnsContainer adjusted>
                      <MultipleActionSetting
                        mapSettingsPanel={mapSettingsPanel}
                        mapSettings={user.mapSettings}
                        setting={{ value: MAP_SETTINGS.problem.TERRITORIES }}
                        handleOnSwitchChange={handleOnSwitchChange}
                        handleOnChangeInput={handleOnChangeInput}
                        key={MAP_SETTINGS.problem.TERRITORIES}
                        noInfoIcon
                        useTableCheckbox={selectedType === OPTIONS.Problem}
                      />
                      <MultipleActionSetting
                        mapSettingsPanel={mapSettingsPanel}
                        mapSettings={user.mapSettings}
                        setting={{ value: MAP_SETTINGS.problem.SKILLS }}
                        handleOnSwitchChange={handleOnSwitchChange}
                        handleOnChangeInput={handleOnChangeInput}
                        key={MAP_SETTINGS.problem.SKILLS}
                        noInfoIcon
                        useTableCheckbox={selectedType === OPTIONS.Problem}
                      />
                      <MultipleActionSetting
                        mapSettingsPanel={mapSettingsPanel}
                        mapSettings={user.mapSettings}
                        setting={{ value: MAP_SETTINGS.problem.CATEGORIES }}
                        handleOnSwitchChange={handleOnSwitchChange}
                        handleOnChangeInput={handleOnChangeInput}
                        key={MAP_SETTINGS.problem.CATEGORIES}
                        noInfoIcon
                        useTableCheckbox={selectedType === OPTIONS.Problem}
                      />
                      <MultipleActionSetting
                        mapSettingsPanel={mapSettingsPanel}
                        mapSettings={user.mapSettings}
                        setting={{ value: MAP_SETTINGS.problem.SPEED_FACTOR }}
                        handleOnSwitchChange={handleOnSwitchChange}
                        handleOnChangeInput={handleOnChangeInput}
                        key={MAP_SETTINGS.problem.SPEED_FACTOR}
                        noInfoIcon
                        useTableCheckbox={selectedType === OPTIONS.Problem}
                      />
                      <MultipleActionSetting
                        mapSettingsPanel={mapSettingsPanel}
                        mapSettings={user.mapSettings}
                        setting={{ value: MAP_SETTINGS.problem.TRAFFIC }}
                        handleOnSwitchChange={handleOnSwitchChange}
                        handleOnChangeInput={handleOnChangeInput}
                        key={MAP_SETTINGS.problem.TRAFFIC}
                        noInfoIcon
                        useTableCheckbox={selectedType === OPTIONS.Problem}
                      />
                      <SingleActionSetting
                        key={MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT}
                        mapSettings={user.mapSettings}
                        mapSettingsPanel={mapSettingsPanel}
                        setting={{ value: MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT }}
                        handleOnSwitchChange={handleOnSwitchChange}
                        useTableCheckbox={selectedType === OPTIONS.Problem}
                      />
                    </StyledContrastPanelColumnsContainer>
                    {user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT] &&
                      user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT]?.enabled !==
                        false && (
                        <LimitFieldsWrapper ref={addVehicleLimitRef}>
                          {limitOptions.map((fieldKey, idx) => (
                            <LimitFieldRow
                              key={fieldKey}
                              style={
                                idx === limitOptions.length - 1 ? { marginBottom: '1.5rem' } : {}
                              }
                            >
                              <Label>
                                {mapSettingsPanel[fieldKey] || fieldKey}
                                {fieldKey === 'maxDistance' ? ' (km)' : ''}
                                {['shiftTime', 'maxDrivingTime'].includes(fieldKey) ? ' (hr)' : ''}:
                              </Label>
                              <Input
                                min={fieldKey === 'maxStops' ? 1 : 0}
                                type="number"
                                value={
                                  limitState[fieldKey][0] !== undefined
                                    ? String(limitState[fieldKey][0])
                                    : ''
                                }
                                onChange={(e) => handleOnChangeLimitInput(fieldKey, e.target.value)}
                                step={
                                  ['maxDrivingTime', 'shiftTime'].includes(fieldKey) ? '0.1' : '1'
                                }
                                inputMode={
                                  ['maxDrivingTime', 'shiftTime'].includes(fieldKey)
                                    ? 'decimal'
                                    : 'numeric'
                                }
                                placeholder={defaultLimitPlaceholders[fieldKey] ?? ''}
                              />
                            </LimitFieldRow>
                          ))}
                        </LimitFieldsWrapper>
                      )}
                  </Section>
                )}
                {!isProd && (
                  <StyledSectionHeader enabled={planEnabled} bordered={!planEnabled}>
                    {mapSettingsPanel.planSectionTitle}
                    <Checkbox
                      id="plan-master-switch"
                      value={planEnabled}
                      label=""
                      onChange={handlePlanSwitch}
                      contrast
                    />
                  </StyledSectionHeader>
                )}
                {planEnabled && (
                  <Section ref={planSectionRef}>
                    <StyledContrastPanelColumnsContainer adjusted>
                      {PLAN_KEYS.map((key) => (
                        <SingleActionSetting
                          key={key}
                          mapSettings={user.mapSettings}
                          mapSettingsPanel={mapSettingsPanel}
                          setting={{ value: key }}
                          handleOnSwitchChange={handleOnSwitchChange}
                          useTableCheckbox={selectedType === OPTIONS.Problem}
                        />
                      ))}
                    </StyledContrastPanelColumnsContainer>
                  </Section>
                )}
              </>
            ) : (
              <Section>
                <StyledContrastPanelColumnsContainer fullModalHeight adjusted>
                  {settings.map((setting) =>
                    setting.value === MAP_SETTINGS.solution.SHOW_TOUR_CONCAVE_HULL ? (
                      <MultipleActionSetting
                        key={setting.value}
                        mapSettingsPanel={mapSettingsPanel}
                        mapSettings={user.mapSettings}
                        setting={setting}
                        handleOnSwitchChange={handleOnSwitchChange}
                        handleOnChangeInput={handleOnChangeInput}
                      />
                    ) : (
                      <SingleActionSetting
                        key={setting.value}
                        mapSettings={user.mapSettings}
                        mapSettingsPanel={mapSettingsPanel}
                        setting={setting}
                        handleOnSwitchChange={handleOnSwitchChange}
                      />
                    ),
                  )}
                </StyledContrastPanelColumnsContainer>
              </Section>
            )}
          </StyledScrollableContent>
          <StyledContrastPanelFooterSettings isDouble isFullWidth mapSettings removeAbsolute>
            <Button
              text={mapSettingsPanel.resetDefaultSettings}
              inverted
              contrast
              onClick={onResetDefault}
            />
            <div />
            <Button
              text={solverConfigLabels.buttonDoneLabel}
              onClick={() => handleDoneClick(selectedType)}
            />
          </StyledContrastPanelFooterSettings>
        </StyledContrastPanel>
      </Modal>
      <StyledAccessContainer
        data-testid="mapSettingsBtn"
        id="map-settings-entry-point"
        onClick={() => togglePanel()}
        order={1}
        icon={mapSettings}
        title={mapSettingsPanel.mapSettingsTitle}
        wizardShown={wizardShown}
      />
    </>
  );
};

export default withTranslation(MapSettingsPanel);
